import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
  X,
  Sparkles,
  Gift,
  ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "octasker_recent_estimates";
const MAX_ESTIMATES = 5;
const EXPIRY_DAYS = 7;

// Helper to get estimates from localStorage
const getStoredEstimates = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const estimates = JSON.parse(stored);
    const now = Date.now();
    const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    
    // Filter out expired estimates
    return estimates.filter(est => (now - est.storedAt) < expiryMs);
  } catch (e) {
    console.error("Error reading stored estimates:", e);
    return [];
  }
};

// Helper to save estimate to localStorage
export const saveEstimateToStorage = (estimateData) => {
  try {
    const existing = getStoredEstimates();
    
    const newEstimate = {
      id: estimateData.checkout_ref,
      email: estimateData.customer_email,
      serviceType: estimateData.service_type || "service",
      total: estimateData.final_total,
      magicLinkUrl: estimateData.magic_link_url,
      magicLinkToken: estimateData.magic_link_token,
      status: "pending",
      storedAt: Date.now(),
      createdAt: new Date().toISOString()
    };
    
    // Add to front, limit to MAX_ESTIMATES
    const updated = [newEstimate, ...existing.filter(e => e.id !== newEstimate.id)].slice(0, MAX_ESTIMATES);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    
    // Dispatch event so other components can react
    window.dispatchEvent(new CustomEvent("estimateStored", { detail: newEstimate }));
    
    return true;
  } catch (e) {
    console.error("Error saving estimate:", e);
    return false;
  }
};

// Helper to remove estimate from storage
const removeEstimateFromStorage = (estimateId) => {
  try {
    const existing = getStoredEstimates();
    const updated = existing.filter(e => e.id !== estimateId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return true;
  } catch (e) {
    console.error("Error removing estimate:", e);
    return false;
  }
};

// Helper to clear all estimates
export const clearAllStoredEstimates = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("estimatesCleared"));
    return true;
  } catch (e) {
    return false;
  }
};

const RecentEstimates = ({ className = "" }) => {
  const [estimates, setEstimates] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Initial load
    setEstimates(getStoredEstimates());
    
    // Listen for new estimates
    const handleNewEstimate = () => {
      setEstimates(getStoredEstimates());
      setDismissed(false); // Show again when new estimate added
    };
    
    const handleCleared = () => {
      setEstimates([]);
    };
    
    window.addEventListener("estimateStored", handleNewEstimate);
    window.addEventListener("estimatesCleared", handleCleared);
    
    return () => {
      window.removeEventListener("estimateStored", handleNewEstimate);
      window.removeEventListener("estimatesCleared", handleCleared);
    };
  }, []);

  const handleDismiss = (estimateId) => {
    removeEstimateFromStorage(estimateId);
    setEstimates(prev => prev.filter(e => e.id !== estimateId));
  };

  const handleDismissAll = () => {
    setDismissed(true);
  };

  const formatServiceType = (type) => {
    if (!type) return "Service";
    return type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Don't render if no estimates or dismissed
  if (estimates.length === 0 || dismissed) {
    return null;
  }

  return (
    <div className={`bg-gradient-to-r from-orange-50 to-amber-50 border-y border-orange-200 py-4 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-600" />
            <h3 className="font-semibold text-slate-800">Your Recent Estimates</h3>
            <Badge variant="outline" className="bg-white text-orange-600 border-orange-300">
              {estimates.length}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismissAll}
            className="text-slate-500 hover:text-slate-700"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {estimates.map((estimate) => (
            <Card 
              key={estimate.id} 
              className="bg-white border-orange-200 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`recent-estimate-${estimate.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge 
                        variant="outline" 
                        className="bg-yellow-50 text-yellow-700 border-yellow-300 text-xs"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        Pending Review
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {formatTime(estimate.createdAt)}
                      </span>
                    </div>
                    
                    <p className="font-medium text-slate-800 truncate">
                      {formatServiceType(estimate.serviceType)}
                    </p>
                    
                    <p className="text-sm text-slate-500 truncate">
                      {estimate.email}
                    </p>
                    
                    <p className="text-lg font-bold text-orange-600 mt-1">
                      ${estimate.total?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDismiss(estimate.id)}
                    className="flex-shrink-0 text-slate-400 hover:text-slate-600 h-6 w-6"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-3">
                  {estimate.magicLinkUrl ? (
                    <a 
                      href={estimate.magicLinkUrl}
                      className="flex-1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button 
                        className="w-full bg-orange-500 hover:bg-orange-600 text-sm"
                        size="sm"
                      >
                        Review & Approve
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </a>
                  ) : estimate.magicLinkToken ? (
                    <Link 
                      to={`/review/${estimate.magicLinkToken}`}
                      className="flex-1"
                    >
                      <Button 
                        className="w-full bg-orange-500 hover:bg-orange-600 text-sm"
                        size="sm"
                      >
                        Review & Approve
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  ) : (
                    <p className="text-xs text-slate-500 italic">
                      Check your email for the review link
                    </p>
                  )}
                </div>

                {/* Signup Credit Reminder */}
                <div className="mt-3 pt-3 border-t border-orange-100">
                  <div className="flex items-center gap-2 text-xs text-orange-700">
                    <Gift className="w-3 h-3" />
                    <span>Create an account after approval to get <strong>$25 off</strong>!</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info text */}
        <p className="text-xs text-slate-500 mt-3 text-center">
          We've also sent you an email with your estimate details. Estimates expire after 30 days.
        </p>
      </div>
    </div>
  );
};

export default RecentEstimates;
